import Big from "big.js";

import { isNotNullOrEmpty, isNotNullOrUndefined, isNullOrEmpty } from "../../../../../Common/TypingUtils";
import { CurrencyHelper } from "../../Internals/ControlsReexport/CustomControls/CurrencyInput/CurrencyHelper";

import { InputContainerProps } from "./InputContainer";
import { InputCommonProps } from "./Input";

type Primitive = undefined | number | string | boolean;

export function fixNumberValue(value: Primitive): string | undefined {
    if (value === undefined || typeof value !== "string" || value === "") {
        // Странная история, по идее value всегда string, но это какой-то legacy-код
        // @ts-ignore
        return value;
    }

    const fixedNumber = trimZerosFromNumberStart(value);

    if (fixedNumber === "+") {
        return "+0";
    } else if (fixedNumber === "-" || fixedNumber === "") {
        return "0";
    }

    return fixedNumber;
}

function trimZerosFromNumberStart(value: string): string {
    return value[0] === "+" || value[0] === "-"
        ? value[0] + value.slice(1).replace(/^0+/, "")
        : value.replace(/^0+/, "");
}

export function fixDecimalValue(value: string | undefined): string | undefined {
    if (value === undefined || typeof value !== "string" || isNaN(Number(value))) {
        return value;
    }
    const formattedValue = CurrencyHelper.unformatString(value);
    if (formattedValue === "") {
        return value;
    }
    return new Big(formattedValue).toFixed();
}

const isPasteOrSomeExternalChange = (from: string, to: string) => Math.abs(from.length - to.length) > 5;

export function wrapWithLimitedByRegexp(
    func: (value: undefined | string) => void,
    inputLimit: undefined | null | string,
    onBlink?: () => void
): (value: undefined | string, prevValue: undefined | string) => void {
    if (!inputLimit) {
        return func;
    }
    const limit = new RegExp(inputLimit);
    return (value: undefined | string, prevValue: undefined | string): void => {
        if (!inputLimit) {
            func(value);
            return;
        }

        const prevCheck = prevValue == undefined ? false : limit.test(prevValue);
        if (!prevCheck) {
            // Не дадим печатать если пользовать как-то получил неправильное значение но продолжает
            // его увеличивать, а стирать -- можно
            const nextValueIncreasesLength = (value ?? "").length > (prevValue ?? "").length;
            if (nextValueIncreasesLength && !isPasteOrSomeExternalChange(prevValue ?? "", value ?? "")) {
                onBlink?.();
            } else {
                func(value);
            }
        }

        // Ну мы типа не будем блокировать ввод если содержимое меняется резко:
        // Это может быть
        //   * встравка из буфера, пользователь может хочет вставить и подправить
        //   * какие-нибудь плагины автоматом вставляют что-нибудь, не будем им мешать
        if (!isPasteOrSomeExternalChange(prevValue ?? "", value ?? "")) {
            const currCheck = value == undefined ? false : limit.test(value);
            if (currCheck) {
                func(value);
            } else {
                onBlink?.();
            }
        } else {
            func(value);
        }
    };
}

export const createNullableMask = (
    value: string | undefined,
    hasError: boolean | undefined,
    { mask, formatChars = defaultFormatChars, rejectInvalidByMaskValue }: InputContainerProps
): string | undefined => {
    if (rejectInvalidByMaskValue) {
        return mask;
    }
    if (isNotNullOrEmpty(mask) && isNotNullOrEmpty(value)) {
        const valueLength = value.length;
        const maskLength = mask.length;
        const isInvalidValue =
            (hasError && valueLength >= maskLength) ||
            (valueLength < maskLength && !hasOnlyAllowedCharacters(value, mask, formatChars));
        return isInvalidValue ? undefined : mask;
    }

    return mask;
};

export const hasOnlyAllowedCharacters = (value: string, mask: string, formatChars: Record<string, string>): boolean =>
    Array.from(value).every((character, position) => isAllowedCharacter(character, mask, formatChars, position));

export const fixDecimalComma = (value: string | null | undefined): string =>
    String(value ?? "").replace(/[,\/\?бю]/i, ".");

const isAllowedCharacter = (character: string, mask: string, formatChars: Record<string, string>, position: number) => {
    if (isNullOrEmpty(character)) {
        return false;
    }

    const ruleChar = mask.charAt(position);
    const charRule = formatChars[ruleChar];
    return (isNotNullOrUndefined(charRule) && new RegExp(charRule).test(character)) || ruleChar === character;
};

export const negativeValueToBrackets = (value: string): string => {
    const currencyInputMinusSign = "−";
    const defaultMinusSign = "-";

    const numberValue = Number(
        fixDecimalComma(value).replace(currencyInputMinusSign, defaultMinusSign).replace(/[\s]/g, "")
    );
    if (isNaN(numberValue) || numberValue >= 0) {
        return value;
    }

    // /^[−-]/
    const minusSignRegexp = new RegExp(`^[${currencyInputMinusSign}${defaultMinusSign}]`);
    return `(${value.replace(minusSignRegexp, "")})`;
};

const defaultFormatChars: Record<string, string> = {
    "9": "[0-9]",
    a: "[A-Za-z]",
    "*": "[A-Za-z0-9]",
};

type FormatDecimalValueOptions = Pick<InputCommonProps, "value" | "currencyFractionDigits">;

function formatDecimal({ value, currencyFractionDigits }: FormatDecimalValueOptions) {
    if (value == undefined || value === "") {
        return "";
    }

    value = CurrencyHelper.unformatString(value);
    const destructed = CurrencyHelper.destructString(value) || {
        sign: "",
        integer: "",
        delimiter: "",
        fraction: "",
    };

    const { sign, integer, delimiter, fraction } = destructed;
    let fractionDigits = fraction.length;

    if (currencyFractionDigits !== null && currencyFractionDigits !== undefined) {
        fractionDigits = currencyFractionDigits;
    }

    let result = integer;

    if (delimiter || fractionDigits) {
        result += ",";
        result += fraction.padEnd(fractionDigits, "0");
    }

    if (sign) {
        result = sign + result;
    }

    return result;
}

type FormatValueOptions = Pick<InputCommonProps, "value" | "dataType" | "currencyFractionDigits">;

export function formatValue(options: FormatValueOptions): string | undefined {
    return options.dataType === "decimal" ? formatDecimal(options) : options.value;
}
